/**
 * HeroCarousel
 *
 * Vanilla JS carousel for the hero-carousel section.
 * - Keyboard navigable (arrow keys on dots tablist)
 * - Autoplay with pause on hover / focus / interaction
 * - RTL-aware translateX direction
 * - Respects prefers-reduced-motion (no autoplay, no transitions)
 * - Swipe via pointer events: the slide follows the finger, then settles
 */
;(function () {
  class HeroCarousel {
    constructor(el) {
      this.el = el
      this.track = el.querySelector('.c-hero-carousel__track')
      this.slides = Array.from(el.querySelectorAll('.c-hero-carousel__slide'))
      this.dots = Array.from(el.querySelectorAll('.c-hero-carousel__dots [role="tab"]'))
      this.prevBtn = el.querySelector('.c-hero-carousel__arrow--prev')
      this.nextBtn = el.querySelector('.c-hero-carousel__arrow--next')
      this.currentIndex = 0
      this.total = this.slides.length
      // Track position, counting the copy of the last slide that leads the
      // track: slide i sits at position i + 1 (see _buildLoop)
      this.position = 1
      this.autoplaySpeed = parseInt(el.dataset.autoplaySpeed || '6', 10) * 1000
      this.autoplayTimer = null
      this.inactivityTimer = null
      this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      this.isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl'

      // Touch / swipe tracking
      this._pointerStartX = null
      this._pointerStartY = null
      this._pointerStartTime = 0
      this._isDragging = false
      this._hasMoved = false
      this._isSliding = false
      this._samples = []

      this.init()
    }

    init() {
      if (this.total <= 1) return

      this._buildLoop()
      this.track.addEventListener('transitionend', (e) => {
        if (e.target === this.track && e.propertyName === 'transform') this._leaveClone()
      })

      // Prev / next buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.prev()
          this._restartInactivityTimer()
        })
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.next()
          this._restartInactivityTimer()
        })
      }

      // Dot buttons — click + keyboard navigation within tablist
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.goTo(i)
          this._restartInactivityTimer()
        })

        dot.addEventListener('keydown', (e) => {
          let target = null
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault()
            target = (i + 1) % this.total
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault()
            target = (i - 1 + this.total) % this.total
          } else if (e.key === 'Home') {
            e.preventDefault()
            target = 0
          } else if (e.key === 'End') {
            e.preventDefault()
            target = this.total - 1
          }
          if (target !== null) {
            this.goTo(target)
            this.dots[target].focus()
            this._restartInactivityTimer()
          }
        })
      })

      // Pause on hover / resume on leave
      this.el.addEventListener('mouseenter', () => this.pauseAutoplay())
      this.el.addEventListener('mouseleave', () => {
        if (!this._isDragging) this.startAutoplay()
      })

      // Pause on focus into carousel / resume on blur out
      this.el.addEventListener('focusin', () => this.pauseAutoplay())
      this.el.addEventListener('focusout', (e) => {
        if (!this.el.contains(e.relatedTarget)) {
          this.startAutoplay()
        }
      })

      // Swipe / pointer events
      this.track.addEventListener('pointerdown', (e) => this._onPointerDown(e))
      this.track.addEventListener('pointermove', (e) => this._onPointerMove(e))
      this.track.addEventListener('pointerup', (e) => this._onPointerUp(e))
      this.track.addEventListener('pointercancel', () => this._onPointerCancel())
      // Axis lock: once a drag is horizontal, the page must not also scroll
      // with the finger's vertical travel. touch-action: pan-y alone lets the
      // browser take the vertical part — Chrome then cancels the drag, iOS
      // Safari scrolls the page under the moving slide — so the touchmoves of
      // a horizontal drag are cancelled, hence non-passive (B2C-327). A
      // gesture that starts vertical is never sliding and scrolls as usual.
      this.track.addEventListener('touchmove', (e) => {
        if (this._isSliding && e.cancelable) e.preventDefault()
      }, { passive: false })
      // Capture phase, so it runs before the slide's <a> acts on the click
      this.track.addEventListener('click', (e) => this._onClick(e), true)

      // Initial render
      this.updateUI()

      // Start autoplay (skipped if reduced motion)
      if (!this.isReducedMotion) this.startAutoplay()
    }

    // An endless loop (B2C-327): a copy of the last slide leads the track and a
    // copy of the first closes it, so "next" on the last slide moves on to the
    // copy of the first, the same way as every other step, then jumps without
    // animation to the real one. It used to rewind across every slide.
    _buildLoop() {
      const lead = this.slides[this.total - 1].cloneNode(true)
      const close = this.slides[0].cloneNode(true)
      for (const copy of [lead, close]) {
        copy.setAttribute('data-hero-clone', '')
        copy.setAttribute('aria-hidden', 'true')
        copy.removeAttribute('aria-label')
        copy.inert = true
      }
      this.track.prepend(lead)
      this.track.append(close)
      this._jump()
    }

    goTo(index) {
      this._leaveClone()
      this.currentIndex = (index + this.total) % this.total
      this.position = this.currentIndex + 1
      this._translate()
      this.updateUI()
    }

    next() {
      this._step(1)
    }

    prev() {
      this._step(-1)
    }

    // One slide on, always the same way; past either end it lands on a copy
    _step(delta) {
      this._leaveClone()
      this.position += delta
      this.currentIndex = (this.position - 1 + this.total) % this.total
      this._translate()
      this.updateUI()
      // No transition, no transitionend: leave the copy straight away
      if (this.isReducedMotion) this._leaveClone()
    }

    // On a copy, jump to the slide it copies. Runs on transitionend, and
    // before any new move, so a step taken mid-animation starts from a real slide.
    _leaveClone() {
      if (this.position !== 0 && this.position !== this.total + 1) return
      this.position = this.currentIndex + 1
      this._jump()
    }

    // Move without animation, and commit it before the transition comes back
    _jump() {
      this.track.classList.add('is-instant')
      this._translate()
      void this.track.offsetWidth
      this.track.classList.remove('is-instant')
    }

    // The track at `position`, shifted by `offset` px while a finger drags it
    _translate(offset = 0) {
      const direction = this.isRTL ? 1 : -1
      const position = `${direction * this.position * 100}%`
      this.track.style.transform = offset
        ? `translateX(calc(${position} + ${offset}px))`
        : `translateX(${position})`
    }

    updateUI() {
      this.slides.forEach((slide, i) => {
        const hidden = i !== this.currentIndex
        slide.setAttribute('aria-hidden', hidden ? 'true' : 'false')
        // Off-screen slides leave the tab order. Focusing one scrolled the
        // overflow:hidden viewport to it, out of step with the dots (B2C-324).
        slide.inert = hidden
      })

      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false')
        dot.setAttribute('tabindex', isActive ? '0' : '-1')
      })

      // Arrows are always enabled (infinite wrap)
      if (this.prevBtn) this.prevBtn.disabled = false
      if (this.nextBtn) this.nextBtn.disabled = false
    }

    startAutoplay() {
      if (this.isReducedMotion) return
      this.pauseAutoplay()
      this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed)
    }

    pauseAutoplay() {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }

    /**
     * After a user interaction (dot click, arrow click), pause autoplay
     * and resume after 9s of inactivity.
     */
    _restartInactivityTimer() {
      this.pauseAutoplay()
      clearTimeout(this.inactivityTimer)
      if (!this.isReducedMotion) {
        this.inactivityTimer = setTimeout(() => this.startAutoplay(), 9000)
      }
    }

    // ---- Pointer / swipe support ----------------------------------------

    _onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      this._pointerStartX = e.clientX
      this._pointerStartY = e.clientY
      this._pointerStartTime = e.timeStamp
      this._isDragging = true
      this._hasMoved = false
      this._isSliding = false
      this._samples = []
      this.pauseAutoplay()
    }

    _onPointerMove(e) {
      if (!this._isDragging) return
      const deltaX = e.clientX - this._pointerStartX
      const deltaY = e.clientY - this._pointerStartY

      if (!this._hasMoved) {
        // Capture the pointer only once it has really moved (10px, so a tap's
        // jitter stays a tap). Capturing on pointerdown retargeted every plain
        // click to the track, so the slide's <a> never received it and no slide
        // link opened (B2C-324). Same order as Horizon's slideshow.js.
        if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return
        this._hasMoved = true
        // Mostly vertical: a page scroll, which touch-action: pan-y leaves to
        // the browser. Let the gesture go.
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this._isDragging = false
          this._restartInactivityTimer()
          return
        }
        this._isSliding = true
        this._leaveClone()
        this.track.setPointerCapture(e.pointerId)
        this.track.classList.add('is-instant')
      }

      // The slide follows the finger (B2C-327); the copies at both ends mean
      // there is always a neighbour to pull in. Recent samples feed the flick
      // test on release: the last ~100ms of movement, not the whole gesture.
      this._samples.push({ x: e.clientX, t: e.timeStamp })
      while (this._samples.length > 1 && e.timeStamp - this._samples[0].t > 100) this._samples.shift()
      this._translate(deltaX)
    }

    // A drag is not a click: the release that ends it must not open the link
    // under the pointer. Reset on the next pointerdown, not on pointerup — the
    // click arrives after pointerup, and a touch swipe sends no click at all,
    // which is also why a keyboard activation (detail 0) is never swallowed.
    _onClick(e) {
      if (!this._hasMoved || e.detail === 0) return
      this._hasMoved = false
      e.preventDefault()
      e.stopImmediatePropagation()
    }

    _onPointerUp(e) {
      if (!this._isDragging) return
      this._isDragging = false

      if (!this._isSliding) {
        // A tap: the click goes on to the slide's link
        this.startAutoplay()
        this._resetPointer()
        return
      }

      // Settle. A flick (the last ~100ms at >= 0.5px/ms, same way as the drag,
      // over >= 30px) moves on, and a fast flick back stays; otherwise the
      // slide moves on once pulled past a quarter of its width.
      const deltaX = e.clientX - this._pointerStartX
      const recent = this._samples[0]
      const recentX = recent ? e.clientX - recent.x : 0
      const speed = recent ? Math.abs(recentX) / Math.max(1, e.timeStamp - recent.t) : 0
      const moveOn = speed >= 0.5
        ? Math.sign(recentX) === Math.sign(deltaX) && Math.abs(deltaX) >= 30
        : Math.abs(deltaX) >= this.track.clientWidth / 4

      this.track.classList.remove('is-instant')
      if (moveOn) {
        const towardsPrev = this.isRTL ? deltaX < 0 : deltaX > 0
        this._step(towardsPrev ? -1 : 1)
      } else {
        this._translate()
      }
      this._restartInactivityTimer()
      this._resetPointer()
    }

    // The browser took the gesture (a page scroll, a native image drag): put
    // the slide back, and let autoplay resume — it used to stay paused for good
    // after a scroll that started on the hero.
    _onPointerCancel() {
      if (this._isSliding) {
        this.track.classList.remove('is-instant')
        this._translate()
      }
      if (this._isDragging) this._restartInactivityTimer()
      this._resetPointer()
    }

    _resetPointer() {
      this._pointerStartX = null
      this._pointerStartY = null
      this._isDragging = false
      this._isSliding = false
      this._samples = []
    }
  }

  // Initialise every carousel once, whatever number of times this file runs.
  //
  // The section emits its own <script src>, so N hero-carousel instances on a
  // page load this file N times. Two guards are needed and neither is optional:
  //
  //   1. The IIFE. `class HeroCarousel` used to sit in the top-level lexical
  //      scope of a classic script, so the second execution threw
  //      "Identifier 'HeroCarousel' has already been declared" and did nothing.
  //      Same idiom as assets/rail-scroll.js (CONSTITUTION VI).
  //   2. The data-attribute flag. Without it the IIFE makes things WORSE: each
  //      execution would register its own DOMContentLoaded listener and every
  //      carousel would get one instance per script tag — duplicate autoplay
  //      timers and duplicate pointer/keyboard listeners fighting each other.
  const init = () => {
    document.querySelectorAll('.c-hero-carousel').forEach((el) => {
      if (el.dataset.heroCarouselReady) return
      el.dataset.heroCarouselReady = 'true'
      new HeroCarousel(el)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
